<template>
  <div class="manager-company-external-offices-ui__filter-wrapper">
    <FilterInput v-model="searchQuery" label="Search" variant-class-name="slim" />
  </div>
  <VTableSortable
    :table="table"
    variation-class-name="manager-company-external-offices slim"
    @sort="onSort"
  >
    <template #col-4="{ colData }">
      <span title="Please note that one opportunity may belong to several offices">{{
        colData
      }}</span>
    </template>
    <template #col-5="{ colData }">
      {{ colData }}
    </template>
    <template #col-6="{ colData }">
      {{ colData }}
    </template>
    <template #col-7="{ colData }">
      <div class="manager-company-external-offices-ui__sync-status">
        <div
          v-if="colData.syncLastEnd"
          title="Last sync end"
          class="manager-company-external-offices-ui__sync-status-entry"
        >
          <span class="manager-company-external-offices-ui__sync-status-entry-label">Last end:</span
          ><span>{{ colData.syncLastEnd }}</span>
        </div>
        <div
          v-if="colData.syncLastStart"
          title="Last sync start"
          class="manager-company-external-offices-ui__sync-status-entry"
        >
          <span class="manager-company-external-offices-ui__sync-status-entry-label"
            >Last start:</span
          ><span>{{ colData.syncLastStart }}</span>
        </div>
      </div>
      <div v-if="!colData.syncLastStart && !colData.syncLastEnd">-</div>
    </template>
    <template #col-8="{ colData }">
      <div>
        <SimpleDropdown
          floating
          placement="bottom-end"
          :auto-resize="false"
          :options="[
            {
              title: 'Sync',
              clickCallback: () => sync(colData.id),
            },
            {
              title: 'Sync properties',
              clickCallback: () => syncProperties(colData.id),
            },
            {
              title: 'Sync all contacts',
              clickCallback: () => syncContacts(colData.id),
            },
            {
              title: 'Sync negotiators',
              clickCallback: () => syncNegotiators(colData.id),
            },
            {
              title: 'Sync vendor contacts',
              clickCallback: () => syncVendorContacts(colData.id),
            },
            {
              title: 'Sync land.',
              clickCallback: () => syncLandlords(colData.id),
            },
            {
              title: 'Sync land. clean',
              clickCallback: () => syncLandlordsClean(colData.id),
            },
            {
              title: 'Sync applicants',
              clickCallback: () => syncApplicants(colData.id),
            },
            {
              title: 'Sync only properties',
              clickCallback: () => syncOnlyProperties(colData.id),
            },
          ]"
        >
          <template #default="{ toggle }">
            <VBtn
              flat
              :loading="syncLoadingMap[colData.id]"
              :disabled="!colData.active"
              class="sync"
              @click="toggle"
              >Actions</VBtn
            >
          </template>
        </SimpleDropdown>
      </div>
    </template>
  </VTableSortable>
  <VPagination v-if="pagination" :pagination="pagination" @on-go-to-page="fetch" />
</template>

<script setup lang="ts">
  // Types
  import type { ITableSortable, TSortingState } from '~/misc/v-table-sortable/types';
  import type {
    TManagerExternalOffice,
    TManagerPaginatedExternalOffices,
    TManagerExternalOfficesIndexPayload,
  } from '~/modules/manager/types';
  import type { TSimplePagination } from '~/misc/resource/types';
  import type { TLoadingMap } from '~/modules/manager/company/external-offices/types';
  import type { ExternalOfficeModel } from '~/modules/manager/models/external-office';

  // Services
  import { ManagerExternalOfficeDataClient } from '~/modules/manager/services/manager-external-office-data-client';

  // Composables
  import { useToast } from '~/composables/use-toast';

  // Config
  import { TABLE_HEADERS } from '~/modules/manager/company/external-offices/config';

  // Components
  import FilterInput from '~/misc/filter-input/FilterInput.vue';
  import VTableSortable from '~/misc/v-table-sortable';
  import VPagination from '~/misc/v-pagination/VPagination.vue';
  import VBtn from '~/misc/v-btn';
  import SimpleDropdown from '~/misc/simple-dropdown/SimpleDropdown.vue';

  // Utils
  import { DateTimeFormatter } from '~/misc/utils/datetime';

  const props = defineProps({
    companyId: {
      type: Number,
      required: true,
    },
  });

  const data: Ref<TManagerPaginatedExternalOffices | null> =
    ref<TManagerPaginatedExternalOffices | null>(null);

  const searchQuery: Ref<string> = ref<string>('');
  const payload: Ref<TManagerExternalOfficesIndexPayload> =
    ref<TManagerExternalOfficesIndexPayload>({});
  const syncLoadingMap: Ref<TLoadingMap> = ref<TLoadingMap>({});

  const table: ComputedRef<ITableSortable> = computed<ITableSortable>(() => ({
    headers: TABLE_HEADERS,
    data:
      data.value?.data.map((office: TManagerExternalOffice) => [
        `${office.name} (${office.external_id})`,
        office.address || 'Unknown',
        office.active ? 'Active' : 'Inactive',
        office.enabled ? 'Yes' : 'No',
        office.opportunities_count,
        office.contacts_count,
        office.users_count,
        {
          syncLastStart: DateTimeFormatter.toDateTime(office.sync_start_at),
          syncLastEnd: DateTimeFormatter.toDateTime(office.sync_end_at),
        },
        { id: office.id, active: office.active },
      ]) || [],
  }));

  const pagination: ComputedRef<TSimplePagination | null> = computed<TSimplePagination | null>(
    () => data.value?.meta || null
  );

  const { error, success } = useToast();

  const dataClient: ManagerExternalOfficeDataClient = new ManagerExternalOfficeDataClient();

  const setPayload = (value: TManagerExternalOfficesIndexPayload) => {
    payload.value = value;
  };

  const setSyncLoadingItem = (id: number, isLoading: boolean) => {
    syncLoadingMap.value[id] = isLoading;
  };

  const fetch = (page: number = 1) => {
    dataClient
      .request<TManagerPaginatedExternalOffices>('index', props.companyId, {
        page,
        ...payload.value,
        q: searchQuery.value,
      })
      .then((response: TManagerPaginatedExternalOffices) => {
        data.value = response;
      })
      .catch(() => {
        error({ message: 'Something went wrong' });
      });
  };

  const sync = (id: number) => runSyncFor(id, 'sync', 'sync');

  const runSyncFor = (
    id: number,
    action: keyof ExternalOfficeModel,
    name: string,
    payload?: object
  ) => {
    setSyncLoadingItem(id, true);

    const args: (number | object)[] = payload ? [id, payload] : [id];

    dataClient
      .request<void>(action, ...args)
      .then(() => {
        success({ message: `${name} is running` });
      })
      .catch(() => {
        error({ message: `Failed to run ${name}` });
      })
      .finally(() => setSyncLoadingItem(id, false));
  };

  const syncProperties = (id: number) => runSyncFor(id, 'syncProperties', 'sync properties');

  const syncContacts = (id: number) => runSyncFor(id, 'syncContacts', 'sync contacts');

  const syncNegotiators = (id: number) => runSyncFor(id, 'syncNegotiators', 'sync negotiators');

  const syncVendorContacts = (id: number) =>
    runSyncFor(id, 'syncVendorContacts', 'sync vendor contacts');

  const syncLandlords = (id: number) =>
    runSyncFor(id, 'syncLandlords', 'sync land.', { clean: false });

  const syncLandlordsClean = (id: number) =>
    runSyncFor(id, 'syncLandlords', 'sync land. clean', { clean: true });

  const syncApplicants = (id: number) => runSyncFor(id, 'syncApplicants', 'sync applicants');

  const syncOnlyProperties = (id: number) =>
    runSyncFor(id, 'syncOnlyProperties', 'sync only properties');

  const onSort = (value: TSortingState | null) => {
    setPayload(value ? { sorting: value } : {});
    fetch(1);
  };

  watch(searchQuery, () => fetch(), { immediate: true });
</script>
